import { JSON_PARSING_ERROR } from 'actions/ApiCaller';
import { pickFetchOptions } from './fetchOptions';
import * as Ctx from './Context';
import { Method } from './types';
import { logError } from './Errors';
import metricsTracker from 'utils/MetricsTracker';
export const UNKNOWN_ACTION = 'action.unknown';
const metricsTag = (config) => (config.action ? `action.${config.action}` : UNKNOWN_ACTION);
const dcRedirectMetricsTag = (config) => config.action ? `dcRedirect.action.${config.action}` : `dcRedirect.${UNKNOWN_ACTION}`;
// MAPI uses this status code to signal to us that the user belongs to
// different data center, and when we hit this status, we'll have to retry the
// API call to the new DC.
const DC_CHANGE_ERROR_CODE = 406;
const handleDCRedirect = ({ response, context }) => {
    if (response.status === DC_CHANGE_ERROR_CODE) {
        metricsTracker.setCount(dcRedirectMetricsTag(context));
        metricsTracker.startTimer(dcRedirectMetricsTag(context));
        // tslint:disable-next-line: no-use-before-declare
        return Ctx.updateHostnameFromPromise({ promise: response.json(), context }).then(refetchWithContextAndTimeout);
    }
    return Promise.resolve({ response, context });
};
const shouldParseResponse = ({ context, response }) => {
    // Table for when the ApiCaller interface is expected to return a parsed
    // object as opposed to non-parsed object
    //
    // status | acceptJson | acceptErrors | response
    //   ok   |     T      |      T       | parsed (ignore acceptError)
    //   ok   |     T      |      F       | parsed (ignore acceptError)
    //   ok   |     F      |      T       | raw response (ignore acceptError)
    //   ok   |     F      |      F       | raw response (ignore acceptError)
    //  !ok   |     T      |      T       | parsed (both acceptjson and accepterrors)
    //  !ok   |     T      |      F       | raw response
    //  !ok   |     F      |      T       | raw response
    //  !ok   |     F      |      F       | raw response
    const { acceptErrors, acceptJson } = context;
    // .ok matches only 2xx responses. We treat anything else as an error.
    // TODO(chirag): Use acceptJson && (response.ok || acceptErrors)
    return (response.ok && acceptJson) || (!response.ok && acceptErrors && acceptJson);
};
// Note: If parsing the body is not necessary, the value will be resolved by
// default. This provides the consumer of this function to then compose it with
// Promise.reject inorder to use this inside a error callback, if the intention
// is to preserve the reject flow.
function parseBody(responseContext) {
    const { response, context } = responseContext;
    const statuscode = response.status || 'UNKNOWN_STATUS_CODE';
    const xReqId = response.headers.get('X-Request-ID');
    const xParentReqId = response.headers.get('X-Parent-Request-ID');
    return response.json().then(resp => {
        const extraData = xReqId || xParentReqId ? { statuscode, xReqId, xParentReqId } : { statuscode };
        const respwithsc = Object.assign({}, resp, extraData);
        // This step is used to fire off an FDP tracking call in cases when
        // META_INFO in response has `abContext` information.
        Ctx.logAbv2InfoInFdp(resp);
        return Promise.resolve(respwithsc);
    }, (err) => {
        // This happens when JSON.parse step had failed due to empty body
        // or other reasons. The `err` object here mostly is a Error object
        // in these cases.
        //
        // TODO: May be map this to sentry so that we can inform upstream
        // services? If so, we might have to add other contextual objects like
        // error, request and response metadata etc.
        const respwithsc = Object.assign({}, err, { statuscode: JSON_PARSING_ERROR, requestUrl: context.url });
        return Promise.reject(respwithsc);
    });
}
function parseBodyIfRequiredAndExtractFromContext(responseContext) {
    const parsedBodyOrResponse = shouldParseResponse(responseContext)
        ? parseBody(responseContext)
        : Promise.resolve(responseContext.response);
    if (responseContext.response.ok) {
        return parsedBodyOrResponse;
    }
    // force-reject if the response from MAPI is a non-200. The type
    // annotation is necessary here because typescript thinks the `res`
    // argument is a PromiseLike<T> | T, whereas we know for sure at this
    // point that that's not the case. We only get the resolved values to
    // the callback, so this essentially forces the typesystem to assume
    // the same.
    return parsedBodyOrResponse.then(res => Promise.reject(res));
}
function trackRequestFinish(responseContext) {
    const { response, context } = responseContext;
    metricsTracker.endTimer(metricsTag(context));
    metricsTracker.endTimer(dcRedirectMetricsTag(context));
    const payloadLength = response.headers.get('x-payload-length') || '';
    if (response.ok && payloadLength) {
        metricsTracker.logSize(`${metricsTag(context)}.size`, Number(payloadLength));
    }
    return responseContext;
}
const fetchWithContextAndTimeout = (context) => {
    const fetchOptions = pickFetchOptions(context);
    if (__DEBUG_ENV__ && __SERVER__) {
        // tslint:disable:no-console
        console.log(`New ApiCaller: Calling ${context.url}`);
    }
    // This function creates the IResponseContext object which gets passed
    // down the chain. This helps get the context object at each step of
    // the chain as the default argument and avoid the more messier pattern
    // of introducting inline anonymous functions. That is:
    //
    // promise.then(someFunc).then(someOtherFunc)
    // vs
    // promise.then(x => someFunc(x)).then(y => someFunc(y))
    const request = fetch(context.url, fetchOptions).then(response => ({ response, context }));
    return Promise.race([request, context.timeout.start]).then(success => {
        context.timeout.clear();
        return Promise.resolve(success);
    }, error => {
        context.timeout.clear();
        return Promise.reject(error);
    });
    // The above `as` is necessary because TS is not intelligent enough to
    // figure out that request is the only possible success case; timeout does
    // not resolve.
};
// alias for readability when this gets used for retry flows
const refetchWithContextAndTimeout = fetchWithContextAndTimeout;
function fetchWithDCRedirection(context) {
    let response = fetchWithContextAndTimeout(context)
        // From this point on, the args in success and error case conform
        // to IResponseContext type.
        // In the first step, we don't care about the error callback
        // because this would happen only during fetch failures (non-HTTP
        // errors like network errors), and we don't handle them right now in
        // anyway, hence this chain passed down the error value. This might
        // return another fetch promise altogether, based on the error code in
        // the first call. In this case though, there is no need to handle
        // further DC switches and treat it as an error.
        .then(handleDCRedirect)
        .then(trackRequestFinish)
        // This step is used to fire off an FDP tracking call and pass through
        // the request context as it is.
        .then(Ctx.logInFdp)
        // Based on user-supplied flags on a per-api basis, we might have
        // to parse the JSON object. This applies for both errors and
        // successes, as any HTTP errors in fetch invoke the success handler.
        // Valid MAPI errors like user login failures need to be handled in the
        // UI and need the parsed response.
        // Also, as a last step, the type of response has to be converted
        // back to either the response object or the promise of parsed data
        // from the ResponseContext object. This function also moves the
        // response/parsed data to the error block based on the status. Any
        // non-success error is treated as error.
        .then(parseBodyIfRequiredAndExtractFromContext)
        // Here, the success case is a passthrough (undefined callback is a
        // no-op and passes the control down to the next chain preserving the
        // promise state), and the error case is a logger for both HTTP errors
        // and network exceptions. Both need to be handled by the logError
        // function.
        .then(undefined, logError(context));
    // TODO: This `as` is required here to workaround the problem of the
    // response type depending on the `acceptJson` flag value. In TS, we can
    // only switch this type based on another type, but not on values. This
    // could be solved by moving acceptJson to enums probably.
    return response;
}
const serializeBody = ({ config, body }) => {
    // TODO: Using an instanceof check here is much better than relying on
    // config.multipart flag because BodyInit can be lots of other things and
    // we aren't really warning/filtering them out here. Calling JSON.stringify
    // on complex objects might not always work the way users intend.
    if (typeof body === 'string' || config.multipart) {
        return body;
    }
    try {
        return JSON.stringify(body);
    }
    catch (e) {
        if (__DEBUG_ENV__) {
            console.error("Passed body couldn't be serialized: ", e);
            return '';
        }
    }
};
// TODO: The response generic type parameter defaults to any (to keep API
// compatibility with the older one), but that's wrong and ends up creating
// holes in type checks. Ideally, `any` has to be replaced by `unknown`,
// which transfers the responsibility of checking the response type by the
// user.
const ApiCaller = {
    get(config) {
        const tag = metricsTag(config);
        metricsTracker.startTimer(tag);
        metricsTracker.setCount(tag);
        config.method = Method.GET;
        return fetchWithDCRedirection(Ctx.cloneWith({ config }));
    },
    put(config, body) {
        const tag = metricsTag(config);
        metricsTracker.startTimer(tag);
        metricsTracker.setCount(tag);
        config.method = Method.PUT;
        return fetchWithDCRedirection(Ctx.cloneWith({ config, body: serializeBody({ config, body }) }));
    },
    post(config, body) {
        const tag = metricsTag(config);
        metricsTracker.startTimer(tag);
        metricsTracker.setCount(tag);
        config.method = Method.POST;
        return fetchWithDCRedirection(Ctx.cloneWith({ config, body: serializeBody({ config, body }) }));
    },
    delete(config) {
        const tag = metricsTag(config);
        metricsTracker.startTimer(tag);
        metricsTracker.setCount(tag);
        config.method = Method.DELETE;
        return fetchWithDCRedirection(Ctx.cloneWith({ config }));
    },
    toString() {
        return 'ApiCaller With Context';
    }
};
export default ApiCaller;
